import { useEffect } from "react";
import type { MantineThemeOverride } from "@mantine/core";
import { useThemeOverrideStore } from "@AppBuilderShared/store/useThemeOverrideStore";
import type { CeruleoAppvTheme } from "./useCeruleoHostedSessionBootstrap";

// Mantine wants a named color as a 10-shade tuple (index 0 lightest → 9 darkest,
// primary shade ~6). Generate a palette from a single hex by mixing toward white
// (lighter shades) and black (darker shades).
function hexToRgb(hex: string): [number, number, number] | null {
  const m = hex.replace("#", "").trim();
  const full =
    m.length === 3
      ? m
          .split("")
          .map((c) => c + c)
          .join("")
      : m;
  if (full.length !== 6) return null;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mix(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `#${[r, g, bl].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

function buildPalette(hex: string): string[] | null {
  const base = hexToRgb(hex);
  if (!base) return null;
  const white: [number, number, number] = [255, 255, 255];
  const black: [number, number, number] = [0, 0, 0];
  // 0..5 lighten toward white, 6 = base, 7..9 darken toward black.
  return [
    mix(base, white, 0.85),
    mix(base, white, 0.7),
    mix(base, white, 0.5),
    mix(base, white, 0.35),
    mix(base, white, 0.2),
    mix(base, white, 0.1),
    hex.startsWith("#") ? hex : `#${hex}`,
    mix(base, black, 0.15),
    mix(base, black, 0.3),
    mix(base, black, 0.45),
  ];
}

const FONT_STYLE_ID = "ceruleo-theme-font";
const VARS_STYLE_ID = "ceruleo-theme-vars";
const OVERLAY_STYLE_ID = "ceruleo-theme-overlay";

// Floating-overlay layout: collapse the AppShell's right grid column so the 3D
// viewport fills the FULL width (full-bleed), then float the parameter panel as
// a translucent "glass" card over the right side. Targets the custom AppShell
// template's grid-area classes via substring match (survives the CSS-module hash).
//
// NOTE: the canvas is deliberately left FULL-BLEED here — no CSS transform.
// Centering the model in the open area beside the panel is done with a real
// ShapeDiver CAMERA offset (see useApplyOverlayCameraOffset), so the canvas
// never vacates the right edge (no white band) and re-frames correctly on
// resize. (The previous translateX(-offset) hack physically shifted the canvas
// left, leaving an uncovered band on the right and not re-centering on resize.)
//
// IMPORTANT: gated to >=48em (desktop/tablet). On mobile the AppShell's own
// responsive layout takes over — forcing the overlay grid there collapses the
// viewport canvas to 0 width (model disappears), so we leave mobile untouched.
function buildOverlayCss(): string {
  return `
@media (min-width: 48em) {
  [class*="mantine-AppShell-main"] {
    grid-template-columns: 1fr !important;
    grid-template-rows: 1fr !important;
    grid-template-areas: "main" !important;
    position: relative !important;
    height: 100% !important;
  }
  [class*="appShellGridAreaMain"] {
    grid-area: main !important;
    height: 100% !important;
    min-height: 100% !important;
  }
  /* The ShapeDiver canvas keeps the width it measured during the initial
     (pre-overlay) sidebar layout, so after the grid collapses to full-bleed it
     leaves an uncovered band on the right (the themed body background shows
     through). Force it to fill its now-full-width container; ShapeDiver's own
     resize observer then re-renders at the correct resolution. */
  [class*="appShellGridAreaMain"] canvas {
    width: 100% !important;
    height: 100% !important;
  }
  [class*="appShellGridAreaRight"] {
    position: absolute !important;
    top: 12px; right: 12px; bottom: auto !important;
    width: min(380px, 34vw);
    /* Size to content, anchored to the top; scroll only when taller than the
       viewport. Fixes short panels (few options) stretching full height. */
    height: auto !important;
    max-height: calc(100vh - 24px) !important;
    background: color-mix(in srgb, var(--mantine-color-body) 70%, transparent) !important;
    -webkit-backdrop-filter: blur(16px); backdrop-filter: blur(16px);
    border: 1px solid color-mix(in srgb, var(--mantine-color-text) 12%, transparent) !important;
    border-inline-start: 1px solid color-mix(in srgb, var(--mantine-color-text) 12%, transparent) !important;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.18);
    overflow-y: auto; z-index: 40;
  }
}`;
}

export function buildCeruleoMantineOverride(
  theme: CeruleoAppvTheme,
): MantineThemeOverride {
  const override: MantineThemeOverride = {};

  const primary = theme.colors?.primary;
  const palette = primary ? buildPalette(primary) : null;
  if (palette) {
    override.colors = { ceruleoPrimary: palette as any };
    override.primaryColor = "ceruleoPrimary";
    override.primaryShade = 6;
  }

  const accent = theme.colors?.accent;
  const accentPalette = accent ? buildPalette(accent) : null;
  if (accentPalette) {
    override.colors = {
      ...(override.colors ?? {}),
      ceruleoAccent: accentPalette as any,
    };
  }

  const fontFamily =
    theme.fonts?.customFontName || theme.fonts?.family || undefined;
  if (fontFamily) {
    const stack = `${JSON.stringify(fontFamily).slice(1, -1)}, system-ui, sans-serif`;
    override.fontFamily = stack;
    override.headings = {
      fontFamily: theme.fonts?.headingFamily
        ? `${theme.fonts.headingFamily}, ${stack}`
        : stack,
    };
  }

  return override;
}

// Applies a Ceruleo AppV theme to the live viewer: Mantine overrides (colors +
// fonts) via the shared theme-override store, plus document-level branding
// (background/surface/text CSS vars, custom @font-face). Layout mode + logo +
// loading icon are handled separately.
export function useApplyCeruleoTheme(theme: CeruleoAppvTheme | undefined): void {
  const setThemeOverride = useThemeOverrideStore(
    (state) => state.setThemeOverride,
  );

  useEffect(() => {
    if (!theme) return;

    setThemeOverride(buildCeruleoMantineOverride(theme));

    // Switch the Mantine color scheme to match the theme so all surface colors
    // (panels, inputs, text) resolve from the correct light/dark palette.
    try {
      document.documentElement.setAttribute(
        "data-mantine-color-scheme",
        theme.lightDarkDefault,
      );
    } catch {
      /* no-op */
    }

    // Custom font @font-face injection.
    if (theme.fonts?.customFontName && theme.fonts?.customFontUrl) {
      let el = document.getElementById(
        FONT_STYLE_ID,
      ) as HTMLStyleElement | null;
      if (!el) {
        el = document.createElement("style");
        el.id = FONT_STYLE_ID;
        document.head.appendChild(el);
      }
      el.textContent = `@font-face { font-family: ${JSON.stringify(theme.fonts.customFontName)}; src: url(${JSON.stringify(theme.fonts.customFontUrl)}); font-display: swap; }`;
    }

    // Document-level color variables (background, surface, text).
    const c = theme.colors ?? {};
    let vars = document.getElementById(
      VARS_STYLE_ID,
    ) as HTMLStyleElement | null;
    if (!vars) {
      vars = document.createElement("style");
      vars.id = VARS_STYLE_ID;
      document.head.appendChild(vars);
    }
    // Override Mantine's scheme-scoped CSS vars with !important so the theme's
    // exact background/text apply regardless of light/dark scheme specificity.
    const rules: string[] = [];
    if (c.background) {
      rules.push(`--ceruleo-bg: ${c.background};`);
      rules.push(`--mantine-color-body: ${c.background} !important;`);
    }
    if (c.surface) rules.push(`--ceruleo-surface: ${c.surface};`);
    if (c.text) {
      rules.push(`--ceruleo-text: ${c.text};`);
      rules.push(`--mantine-color-text: ${c.text} !important;`);
    }
    // Inputs/buttons (Select, etc.) draw their border from Mantine's
    // --mantine-color-default-border, which resolves to a light gray in the LIGHT
    // scheme. On a dark-background theme that reads as pale "white" outlines
    // around controls. Derive the border from the theme's own text color so
    // controls always sit correctly against the surface, in either scheme.
    rules.push(
      `--mantine-color-default-border: color-mix(in srgb, var(--mantine-color-text) 22%, transparent) !important;`,
    );
    const sel =
      ':root, [data-mantine-color-scheme="light"], [data-mantine-color-scheme="dark"]';
    vars.textContent = rules.length ? `${sel} { ${rules.join(" ")} }` : "";

    // Floating-overlay layout (vs default sidebar).
    let overlay = document.getElementById(
      OVERLAY_STYLE_ID,
    ) as HTMLStyleElement | null;
    if (!overlay) {
      overlay = document.createElement("style");
      overlay.id = OVERLAY_STYLE_ID;
      document.head.appendChild(overlay);
    }
    overlay.textContent = theme.layoutMode === "overlay" ? buildOverlayCss() : "";
  }, [theme, setThemeOverride]);
}
